<template>
  <DpaNewsDatepicker
    v-model="model"
    :custom-format-callback="formatCallback"
    @date-changed="handleDateChange"
    @quick-filter-changed="handleQuickFilterChanged"
    @reset="handleReset" />
</template>

<script setup lang="ts">
import { format } from "date-fns";
import { storeToRefs } from "pinia";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import useFilterSelectorStore from "@/stores/search/filterSelector.ts";
import type { PresetRange } from "@/stores/search.ts";
import type { RelativeDateRange } from "@/types/enums.ts";
import {
  getTimezone,
  isDateRange,
  relativeDateLookup,
} from "@/utils/dateRangeUtils.ts";

import DpaNewsDatepicker from "../DpaNewsDatepicker/DpaNewsDatepicker.vue";

import type { FilterSelectorItemWrapperProps } from "./filterSelectorItemWrapper";

defineProps<FilterSelectorItemWrapperProps>();

const filterSelectorStore = useFilterSelectorStore();
const { selectedDate, selectedRelativeDate } = storeToRefs(filterSelectorStore);
const model = ref<[Date, Date]>();

const { t } = useI18n();

const formatCallback = (value: Date | [Date, Date]): string => {
  const dateFormat = "dd.MM.y";
  if (!isDateRange(value)) return "";
  if (selectedRelativeDate.value && selectedRelativeDate.value.length > 0) {
    return t(`ui.datepicker.${selectedRelativeDate.value[0]}`);
  }
  return `${format(value[0], dateFormat)} - ${format(value[1], dateFormat)}`;
};

function handleDateChange(newDate: [Date, Date]) {
  filterSelectorStore.resetSelectedRelativeDate();
  filterSelectorStore.setSelectedDate(newDate);
}

function handleQuickFilterChanged(quickFilter: PresetRange) {
  filterSelectorStore.resetSelectedDate();
  filterSelectorStore.setSelectedRelativeDate([
    quickFilter.name as RelativeDateRange,
    getTimezone(),
  ]);
}

function handleReset() {
  filterSelectorStore.resetSelectedDate();
  filterSelectorStore.resetSelectedRelativeDate();
}

watch(
  [selectedDate, selectedRelativeDate],
  ([newSelectedDate, newSelectedRelativeDate]) => {
    if (newSelectedDate !== undefined) {
      model.value = newSelectedDate;
    } else if (newSelectedRelativeDate !== undefined) {
      model.value = relativeDateLookup[newSelectedRelativeDate[0]];
    } else {
      model.value = undefined;
    }
  },
  { immediate: true },
);
</script>
